import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articles, type Article } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL;

function buildArticleJsonLd(article: Article) {
  const graph: Record<string, unknown>[] = [];

  graph.push({
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: { '@type': 'Organization', name: 'OOPBUY Spreadsheet' },
    datePublished: '2026-07-15',
    dateModified: '2026-07-15',
    inLanguage: 'de',
    publisher: {
      '@type': 'Organization',
      name: 'OOPBUY Spreadsheet',
      url: SITE_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/artikel/${article.slug}`,
    },
  });

  graph.push({
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Startseite',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: `${SITE_URL}/artikel/${article.slug}`,
      },
    ],
  });

  const hasFaq = article.sections.some((s) => s.type === 'faq');
  if (hasFaq) {
    const faqSections = article.sections.filter((s) => s.type === 'faq');
    const allFaqItems = faqSections.flatMap((s) => s.faqItems || []);
    graph.push({
      '@type': 'FAQPage',
      mainEntity: allFaqItems.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    const found = articles.find((a) => a.slug === slug);
    if (!found) {
      navigate('/404', { replace: true });
      return;
    }
    setArticle(found);
  }, [slug, navigate]);

  if (!article) return null;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildArticleJsonLd(article)) }}
      />
      <Navbar />
      <main className="pt-20 pb-16 md:pb-24">
        <article className="w-full max-w-[780px] mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Zurück zur Startseite
            </Link>
          </nav>

          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">Lesezeit: {article.readingTime}</span>
              <span className="text-xs text-foreground-400">{article.date}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              <strong>OOPBUY Spreadsheet</strong>: {article.title.split(': ')[1] || article.title}
            </h1>
            <div className="w-full aspect-[16/9] rounded-2xl overflow-hidden mb-6">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.description}
            </p>
          </header>

          <div className="space-y-8">
            {article.sections.map((section, idx) => {
              if (section.type === 'text' || section.type === 'conclusion') {
                return (
                  <section key={idx}>
                    <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                      {section.title}
                    </h2>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      {section.content}
                    </p>
                  </section>
                );
              }

              if (section.type === 'list') {
                return (
                  <section key={idx}>
                    <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                      {section.title}
                    </h2>
                    <ul className="list-disc pl-5 space-y-2 text-sm md:text-base text-foreground-700">
                      {section.items?.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </section>
                );
              }

              if (section.type === 'table') {
                return (
                  <section key={idx}>
                    <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                      {section.title}
                    </h2>
                    <div className="overflow-x-auto rounded-xl border border-background-200">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-background-100">
                            {section.tableHeaders?.map((h, i) => (
                              <th
                                key={i}
                                className={`text-left px-4 py-3 font-semibold ${
                                  i === 0
                                    ? 'text-foreground-800'
                                    : i === 1
                                      ? 'text-primary-600'
                                      : 'text-foreground-500'
                                }`}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-background-200">
                          {section.tableRows?.map((row, i) => (
                            <tr key={i}>
                              <td className="px-4 py-3 font-medium text-foreground-800">{row.label}</td>
                              <td className="px-4 py-3 text-foreground-700">{row.withOopbuy}</td>
                              <td className="px-4 py-3 text-foreground-500">{row.withoutOopbuy}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              }

              if (section.type === 'faq') {
                return (
                  <section key={idx}>
                    <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                      {section.title}
                    </h2>
                    <div className="space-y-3">
                      {section.faqItems?.map((faq, i) => (
                        <div key={i} className="bg-background-100 rounded-xl p-5">
                          <h4 className="font-semibold text-foreground-900 mb-2">{faq.question}</h4>
                          <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              }

              return null;
            })}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-foreground-700 border border-foreground-300/50 rounded-full hover:bg-background-100 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                Zurück zur Startseite
              </Link>
              <div className="flex flex-wrap gap-3">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 4)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/artikel/${a.slug}`}
                      className="text-xs text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      {a.category}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}